import React from 'react';
import { useQuery } from 'react-query';
import {
  FiUsers,
  FiBriefcase,
  FiDollarSign,
  FiActivity,
  FiAlertTriangle,
  FiTrendingUp,
  FiFileText,
} from 'react-icons/fi';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { lraService } from '../../services/apiService';
import { useAuthStore } from '../../store/authStore';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatCard from '../../components/StatCard';
import { formatCurrency, formatDateTime, formatDate } from '../../utils/formatters';

const Intelligence = () => {
  const lraUser = useAuthStore((state) => state.lraUser);
  const lraDetails = useAuthStore((state) => state.lraDetails);

  // Main intelligence data
  const {
    data: intelData,
    isLoading: loadingIntel,
    error: intelError,
  } = useQuery('lra-intelligence', lraService.getIntelligence, {
    refetchInterval: 60000, // refresh every minute
  });

  // Revenue trend (30 days)
  const { data: revenueData, isLoading: loadingRevenue } = useQuery(
    ['lra-revenue-trend', 30],
    () => lraService.getRevenueTrend(30)
  );

  // Registration trend (30 days)
  const { data: regData } = useQuery(['lra-registration-trend', 30], () =>
    lraService.getRegistrationTrend(30)
  );

  // Divisions breakdown
  const { data: divisionsData } = useQuery(
    'lra-divisions',
    lraService.getDivisions
  );

  // Recent activity
  const { data: activityData } = useQuery(['lra-recent-activity', 15], () =>
    lraService.getRecentActivity(15)
  );

  if (loadingIntel) return <LoadingSpinner message="Loading intelligence..." />;

  if (intelError) {
    return (
      <div className="empty-state">
        <FiAlertTriangle size={48} />
        <h3>Failed to load intelligence</h3>
        <p>{intelError.message}</p>
      </div>
    );
  }

  const intel = intelData?.data || {};
  const revenueTrend = revenueData?.data || [];
  const regTrend = regData?.data || [];
  const divisions = divisionsData?.data || [];
  const activity = activityData?.data || [];

  const today = intel.today || {};
  const taxpayers = intel.taxpayers || {};
  const filings = intel.filings || {};
  const compliance = intel.compliance || {};
  const audit = intel.audit || {};

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">National Revenue Intelligence</h1>
          <p className="page-subtitle">
            {lraDetails?.title || 'LRA Officer'} —{' '}
            {lraDetails?.division || 'All Divisions'}
            {lraDetails?.jurisdiction &&
              ` • ${lraDetails.jurisdiction}`}
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            Last updated
          </div>
          <div style={{ fontSize: '14px', fontWeight: 600 }}>
            {formatDateTime(new Date().toISOString())}
          </div>
        </div>
      </div>

      {/* Top stat cards */}
      <div className="stats-grid">
        <StatCard
          icon={<FiBriefcase size={24} />}
          value={taxpayers.with_tin || 0}
          label="Registered Taxpayers"
          color="blue"
        />
        <StatCard
          icon={<FiActivity size={24} />}
          value={taxpayers.active_30d || 0}
          label="Active (30 days)"
          color="purple"
        />
        <StatCard
          icon={<FiDollarSign size={24} />}
          value={formatCurrency(today.captured_sales || 0)}
          label="Today's Captured Sales"
          color="green"
        />
        <StatCard
          icon={<FiFileText size={24} />}
          value={formatCurrency(today.tax_collected || 0)}
          label="Today's Tax Collected"
          color="orange"
        />
      </div>

      {/* Second row of stats */}
      <div className="stats-grid" style={{ marginTop: 0 }}>
        <StatCard
          icon={<FiTrendingUp size={24} />}
          value={today.transactions || 0}
          label="Today's Transactions"
          color="blue"
        />
        <StatCard
          icon={<FiFileText size={24} />}
          value={filings.total || 0}
          label="Filings Submitted"
          color="purple"
        />
        <StatCard
          icon={<FiAlertTriangle size={24} />}
          value={audit.high_risk || 0}
          label="High-Risk Flags"
          color="red"
        />
        <StatCard
          icon={<FiUsers size={24} />}
          value={compliance.compliant || 0}
          label="Compliant Businesses"
          color="green"
        />
      </div>

      {/* Charts row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        {/* Revenue trend */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Revenue Trend (30 days)</h2>
          </div>
          {loadingRevenue ? (
            <LoadingSpinner />
          ) : revenueTrend.length === 0 ? (
            <div className="empty-state">
              <FiTrendingUp size={40} />
              <p>No revenue data yet</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={revenueTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => {
                    const dt = new Date(d);
                    return `${dt.getMonth() + 1}/${dt.getDate()}`;
                  }}
                  fontSize={11}
                />
                <YAxis fontSize={11} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(label) => formatDate(label)}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="sales"
                  name="Sales"
                  stroke="#1a73e8"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
                <Line
                  type="monotone"
                  dataKey="tax"
                  name="Tax"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Registration trend */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">New Registrations (30 days)</h2>
          </div>
          {regTrend.length === 0 ? (
            <div className="empty-state">
              <FiUsers size={40} />
              <p>No registration data yet</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={regTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => {
                    const dt = new Date(d);
                    return `${dt.getMonth() + 1}/${dt.getDate()}`;
                  }}
                  fontSize={11}
                />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip
                  labelFormatter={(label) => formatDate(label)}
                />
                <Bar
                  dataKey="registrations"
                  name="Registrations"
                  fill="#22c55e"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Divisions & Activity row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        {/* Divisions breakdown */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Tax Divisions</h2>
          </div>
          {divisions.length === 0 ? (
            <div className="empty-state">
              <FiBriefcase size={40} />
              <p>No division data</p>
            </div>
          ) : (
            <div style={{ padding: '4px 0' }}>
              {divisions.map((d, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom:
                      i < divisions.length - 1
                        ? '1px solid #f3f4f6'
                        : 'none',
                  }}
                >
                  <div>
                    <div
                      style={{ fontWeight: 600, textTransform: 'capitalize' }}
                    >
                      {(d.division || 'Unclassified').replace(/_/g, ' ')}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {d.verified} verified
                    </div>
                  </div>
                  <div
                    style={{
                      background: '#e8f0fe',
                      color: '#1a73e8',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontWeight: 700,
                      fontSize: '14px',
                    }}
                  >
                    {d.businesses}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Live Activity Feed</h2>
          </div>
          {activity.length === 0 ? (
            <div className="empty-state">
              <FiActivity size={40} />
              <p>No recent activity</p>
            </div>
          ) : (
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {activity.map((a, i) => (
                <div
                  key={a.sale_id || i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom:
                      i < activity.length - 1
                        ? '1px solid #f3f4f6'
                        : 'none',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {a.business_name || a.dssn}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginTop: 2,
                      }}
                    >
                      {a.invoice_number || a.sale_id}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', marginLeft: 12 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        color: '#22c55e',
                        fontSize: '14px',
                      }}
                    >
                      {formatCurrency(a.total_amount)}
                    </div>
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#9ca3af',
                        marginTop: 2,
                      }}
                    >
                      {formatDateTime(a.sale_date)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Compliance & Audit row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '24px',
          marginTop: '24px',
        }}
      >
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Compliance Overview</h2>
          </div>
          <ComplianceRow
            label="Compliant"
            value={compliance.compliant || 0}
            total={compliance.total || 0}
            color="#22c55e"
          />
          <ComplianceRow
            label="Under Review"
            value={compliance.under_review || 0}
            total={compliance.total || 0}
            color="#f59e0b"
          />
          <ComplianceRow
            label="Non-Compliant"
            value={compliance.non_compliant || 0}
            total={compliance.total || 0}
            color="#ef4444"
          />
        </div>

        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Audit Signals</h2>
          </div>
          <ComplianceRow
            label="Open"
            value={audit.open || 0}
            total={(audit.open || 0) + (audit.reviewing || 0) + 1}
            color="#f59e0b"
          />
          <ComplianceRow
            label="Reviewing"
            value={audit.reviewing || 0}
            total={(audit.open || 0) + (audit.reviewing || 0) + 1}
            color="#1a73e8"
          />
          <ComplianceRow
            label="High-Risk"
            value={audit.high_risk || 0}
            total={(audit.open || 0) + (audit.reviewing || 0) + 1}
            color="#ef4444"
          />
        </div>
      </div>
    </div>
  );
};

// ---------- Helper components ----------

const ComplianceRow = ({ label, value, total, color }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div style={{ padding: '10px 0' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: '13px', color: '#374151' }}>{label}</span>
        <span style={{ fontWeight: 600, fontSize: '13px', color }}>
          {value}
        </span>
      </div>
      <div
        style={{
          height: 6,
          background: '#f3f4f6',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: color,
            transition: 'width 0.3s',
          }}
        />
      </div>
    </div>
  );
};

export default Intelligence;
